import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { ImageUploadField } from "@/components/ImageUploadField";

export function AddCountryDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) throw new Error("Not authenticated");
      if (!heroImageUrl) throw new Error("Hero image is required");

      const { error } = await supabase
        .from("countries")
        .insert([{ 
          name, 
          hero_image_url: heroImageUrl,
          user_id: session.user.id 
        }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("Country added successfully");
      setOpen(false);
      setName("");
      setHeroImageUrl(null);
    },
    onError: () => {
      toast.error("Failed to add country");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" type="button">
          <Plus className="h-3 w-3 mr-1" />
          Add Country
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Country</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="space-y-4">
          <ImageUploadField
            value={heroImageUrl}
            onChange={setHeroImageUrl}
            entityType="destination"
            required
          />
          <div>
            <Label htmlFor="country-name">Country Name *</Label>
            <Input
              id="country-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Botswana"
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending || !heroImageUrl || !name}>
              {createMutation.isPending ? "Adding..." : "Add"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
